import type { TextFieldVariants } from '@mui/material'
import { InputAdornment } from '@mui/material'
import React from 'react'
import { NumericFormat } from 'react-number-format'
import { ComponentSourceType } from 'types/components'
import { currencySymbols } from 'utils/currency'
import { TextField } from '../@extended/TextField'

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
  decimalScale?: number
  fixedDecimalScale?: boolean
}

const NumericFormatCustom = function NumericFormatCustom({
  ref,
  ...props
}: CustomProps & { ref?: React.RefObject<HTMLInputElement | null> }) {
  const { onChange, decimalScale, fixedDecimalScale, ...other } = props

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values: { value: string }) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        })
      }}
      thousandSeparator
      valueIsNumericString
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
    />
  )
}

interface CurrencyFieldProps {
  id?: string
  label?: string
  value: string
  onChange: (value: any) => void
  name: string
  currency: string
  currencyDecimals?: number
  decimalPlaces?: number
  disabled?: boolean
  placeholder?: string
  style?: React.CSSProperties
  variant?: TextFieldVariants
  pill?: boolean
  shrink?: boolean
  sourceType?: ComponentSourceType
  allowEmpty?: boolean
}

const CurrencyField: React.FC<CurrencyFieldProps> = ({
  id,
  label,
  value,
  onChange,
  name,
  currency,
  currencyDecimals,
  decimalPlaces,
  disabled = false,
  placeholder,
  style,
  variant = 'outlined',
  pill = false,
  shrink = false,
  sourceType = ComponentSourceType.COMPONENT_SOURCE_MANUAL,
  allowEmpty = false,
}) => {
  const symbol = currencySymbols[currency] || currency

  if (sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
    // Handle composite input - allow list of values
    return (
      <TextField
        id={id}
        key={`${id}-currency-field-composite`}
        fullWidth
        label={label}
        value={value?.toString() ?? ''}
        onChange={(e) => onChange(e.target.value)}
        name={name}
        rows={4}
        disabled={disabled}
        placeholder={placeholder || 'Enter values, separated by commas'}
        style={style}
        variant={variant}
        pill={pill}
        shrink={shrink}
      />
    )
  }

  // Original implementation for manual input
  const inputDecimalPlaces = decimalPlaces !== undefined ? decimalPlaces : currencyDecimals

  return (
    <TextField
      id={id}
      key={`${id}-currency-field`}
      fullWidth
      label={label}
      value={value?.toString() ?? ''}
      onChange={(e) => onChange(e.target.value)}
      name={name}
      InputProps={{
        inputComponent: NumericFormatCustom as any,
        inputProps: {
          decimalScale: inputDecimalPlaces,
          fixedDecimalScale: !allowEmpty,
          allowEmptyFormatting: allowEmpty,
        },
        startAdornment: <InputAdornment position="start">{symbol}</InputAdornment>,
        sx: {
          '& input': {
            textAlign: 'right',
          },
        },
      }}
      disabled={disabled}
      placeholder={placeholder}
      style={style}
      variant={variant}
      pill={pill}
      shrink={shrink}
    />
  )
}

export default CurrencyField
